(function () {
  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  const COMPRESS_OVER = 700 * 1024;
  const ALLOWED_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'application/pdf',
  ];

  // Hosts that return a public URL with browser CORS (no API key).
  const UPLOAD_HOSTS = [
    {
      name: 'tmpfile',
      upload: async (file) => {
        const fd = new FormData();
        fd.append('file', file, file.name);
        const res = await fetch('https://tmpfile.link/api/upload', {
          method: 'POST',
          body: fd,
        });
        const data = await res.json().catch(() => null);
        const url = data?.downloadLinkEncoded || data?.downloadLink || data?.url;
        if (!res.ok || !url) throw new Error('tmpfile_failed');
        return String(url);
      },
    },
    {
      name: 'easysend',
      upload: async (file) => {
        const fd = new FormData();
        fd.append('file', file, file.name);
        const res = await fetch('https://easysend.co/api/v1/upload', {
          method: 'POST',
          body: fd,
        });
        const data = await res.json().catch(() => null);
        const url = data?.url || data?.download_url || data?.link || data?.data?.url;
        if (!res.ok || !url) throw new Error('easysend_failed');
        return String(url);
      },
    },
  ];

  function showStatus(section, message, type) {
    const statusEl = section.querySelector('[data-custom-order-status]');
    if (!statusEl) return;

    statusEl.textContent = message;
    statusEl.classList.remove(
      'visually-hidden',
      'custom-orders__form-status--success',
      'custom-orders__form-status--error'
    );
    statusEl.classList.add('is-visible', `custom-orders__form-status--${type}`);
    statusEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function setSubmitting(form, busy, label) {
    const button = form.querySelector('[data-custom-order-submit]');
    form.classList.toggle('is-submitting', busy);
    if (!button) return;
    if (busy) {
      if (!button.dataset.defaultLabel) {
        button.dataset.defaultLabel = button.textContent.trim();
      }
      button.textContent = label || 'Αποστολή…';
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    } else {
      button.textContent = button.dataset.defaultLabel || button.textContent;
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  function resetFileUI(section) {
    const fileNameEl = section.querySelector('[data-custom-order-file-name]');
    const dropzone = section.querySelector('[data-custom-order-dropzone]');
    const fileInput = section.querySelector('[data-custom-order-file]');
    if (fileNameEl) fileNameEl.textContent = '';
    if (dropzone) dropzone.classList.remove('has-file');
    if (fileInput) fileInput.value = '';
  }

  function resetForm(form, section) {
    form.reset();
    resetFileUI(section);
    const karatSelect = form.querySelector('[data-custom-order-karat]');
    if (karatSelect) karatSelect.selectedIndex = 0;
    const attachmentField = form.querySelector('[data-custom-order-attachment-url]');
    if (attachmentField) attachmentField.value = '';
  }

  function validateFile(file) {
    if (!file) return null;
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Μη υποστηριζόμενος τύπος αρχείου. Χρησιμοποιήστε JPG, PNG, WEBP ή PDF.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'Το αρχείο είναι πολύ μεγάλο. Μέγιστο μέγεθος: 5 MB.';
    }
    return null;
  }

  function formatBytes(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function updateFileName(section, file) {
    const fileNameEl = section.querySelector('[data-custom-order-file-name]');
    const dropzone = section.querySelector('[data-custom-order-dropzone]');
    if (!fileNameEl) return;
    if (file) {
      fileNameEl.textContent = `${file.name} · ${formatBytes(file.size)}`;
      if (dropzone) dropzone.classList.add('has-file');
    } else {
      fileNameEl.textContent = '';
      if (dropzone) dropzone.classList.remove('has-file');
    }
  }

  function parseContactResponse(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const success = Boolean(
      doc.querySelector(
        '.form-status-list.form__message, h2.form-status-list, .form-status.form-status-list, [class*="form-status-list"]'
      )
    );
    const errorNode = doc.querySelector(
      '.contact__field-error, .form__message .form-status[role="alert"], .errors, .form-status[role="alert"]'
    );
    const hasErrorText = Boolean(
      errorNode && /error|σφάλμα|πρόβλημα|invalid|blank/i.test(errorNode.textContent || '')
    );

    if (success && !hasErrorText) return { ok: true };

    // Shopify often redirects to ?contact_posted=true
    if (/contact_posted=true|form_type=contact/i.test(html) && /posted_successfully|ευχαριστ/i.test(html)) {
      return { ok: true };
    }

    const messages = Array.from(
      doc.querySelectorAll('.form-status-list .link, .contact__field-error .form__message, .errors ul li')
    )
      .map((node) => node.textContent.trim())
      .filter(Boolean);

    return { ok: false, message: messages.join(' ') || null };
  }

  function compressImage(file) {
    if (!file.type.startsWith('image/') || file.type === 'image/gif') {
      return Promise.resolve(file);
    }
    if (file.size <= COMPRESS_OVER) {
      return Promise.resolve(file);
    }

    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        const maxSide = 1400;
        let { width, height } = img;
        if (width > maxSide || height > maxSide) {
          const scale = maxSide / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(objectUrl);
        canvas.toBlob(
          (blob) => {
            if (!blob || blob.size >= file.size) {
              resolve(file);
              return;
            }
            resolve(new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }));
          },
          'image/jpeg',
          0.8
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(file);
      };
      img.src = objectUrl;
    });
  }

  async function uploadAttachment(file) {
    const ready = await compressImage(file);
    let lastError = null;
    for (const host of UPLOAD_HOSTS) {
      try {
        const url = await host.upload(ready);
        if (/^https?:\/\//i.test(url)) return { url, name: ready.name };
      } catch (err) {
        lastError = err;
      }
    }
    throw lastError || new Error('upload_failed');
  }

  async function submitShopifyContact(section, form) {
    const successMessage = section.dataset.successMessage;
    const errorMessage = section.dataset.errorMessage;
    const contactEmail = section.dataset.contactEmail || 'info@kewels.com';

    const karatSelect = form.querySelector('[data-custom-order-karat]');
    if (karatSelect && !karatSelect.value) {
      showStatus(section, 'Παρακαλούμε επιλέξτε καράτια χρυσού.', 'error');
      karatSelect.focus();
      return;
    }

    const fileInput = form.querySelector('[data-custom-order-file]');
    const file = fileInput?.files?.[0];
    const fileError = validateFile(file);
    if (fileError) {
      showStatus(section, fileError, 'error');
      return;
    }

    const bodyField = form.querySelector('[data-custom-order-body]');
    const attachmentField = form.querySelector('[data-custom-order-attachment-url]');
    const originalBody = bodyField ? bodyField.value : '';

    setSubmitting(form, true, file ? 'Ανέβασμα αρχείου…' : 'Αποστολή…');

    try {
      if (file) {
        const uploaded = await uploadAttachment(file);
        if (attachmentField) attachmentField.value = uploaded.url;
        if (bodyField) {
          bodyField.value =
            `${originalBody.trim()}\n\n` +
            `========== ΣΥΝΗΜΜΕΝΟ ==========\n` +
            `Αρχείο: ${uploaded.name}\n` +
            `Κατεβάστε εδώ: ${uploaded.url}\n` +
            `(ο σύνδεσμος ισχύει για μερικές ημέρες)\n` +
            `===============================`;
        }
        setSubmitting(form, true, 'Αποστολή…');
      } else if (attachmentField) {
        attachmentField.value = '';
      }

      const formData = new FormData(form);
      // Shopify contact δεν δέχεται file inputs
      for (const key of [...formData.keys()]) {
        const values = formData.getAll(key);
        if (values.some((v) => typeof File !== 'undefined' && v instanceof File)) {
          formData.delete(key);
        }
      }

      const response = await fetch(form.getAttribute('action') || window.location.pathname, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'text/html' },
        redirect: 'follow',
      });

      const html = await response.text();
      const result = parseContactResponse(html);
      const postedFlag =
        /contact_posted=true/i.test(response.url || '') ||
        /contact_posted=true/i.test(html) ||
        result.ok;

      if (response.ok && postedFlag) {
        const msg = file
          ? `${successMessage} Το συνημμένο στάλθηκε ως σύνδεσμος στο email.`
          : successMessage;
        showStatus(section, msg, 'success');
        resetForm(form, section);
        return;
      }

      // Fallback: κάποια themes επιστρέφουν 200 χωρίς flag — αν δεν υπάρχει error markup, δεχόμαστε
      if (response.ok && !result.message && !/role="alert"|contact__field-error/i.test(html)) {
        showStatus(section, successMessage, 'success');
        resetForm(form, section);
        return;
      }

      showStatus(section, result.message || errorMessage, 'error');
    } catch (error) {
      console.error('[custom-order]', error);
      if (file) {
        showStatus(
          section,
          `Δεν ανέβηκε το αρχείο. Στείλτε το απευθείας στο ${contactEmail} μαζί με το αίτημά σας.`,
          'error'
        );
      } else {
        showStatus(section, errorMessage, 'error');
      }
    } finally {
      if (bodyField) bodyField.value = originalBody;
      if (attachmentField) attachmentField.value = '';
      setSubmitting(form, false);
    }
  }

  function initializeCustomOrderForms(rootEl = document) {
    const sections = rootEl.querySelectorAll('[data-custom-orders-section]');

    sections.forEach((section) => {
      const form = section.querySelector('[data-custom-order-form]');
      const fileInput = section.querySelector('[data-custom-order-file]');
      const dropzone = section.querySelector('[data-custom-order-dropzone]');
      const clearBtn = section.querySelector('[data-custom-order-file-clear]');

      if (!form || form.dataset.customOrderBound === 'true') return;
      form.dataset.customOrderBound = 'true';

      if (fileInput) {
        fileInput.addEventListener('change', () => {
          const selected = fileInput.files?.[0];
          const err = validateFile(selected);
          if (err) {
            showStatus(section, err, 'error');
            resetFileUI(section);
            return;
          }
          updateFileName(section, selected);
        });
      }

      if (clearBtn && fileInput) {
        clearBtn.addEventListener('click', (event) => {
          event.preventDefault();
          resetFileUI(section);
        });
      }

      if (dropzone && fileInput) {
        ['dragenter', 'dragover'].forEach((type) => {
          dropzone.addEventListener(type, (event) => {
            event.preventDefault();
            dropzone.classList.add('is-dragover');
          });
        });
        ['dragleave', 'drop'].forEach((type) => {
          dropzone.addEventListener(type, (event) => {
            event.preventDefault();
            dropzone.classList.remove('is-dragover');
          });
        });
        dropzone.addEventListener('drop', (event) => {
          const dropped = event.dataTransfer?.files?.[0];
          if (!dropped) return;
          const err = validateFile(dropped);
          if (err) {
            showStatus(section, err, 'error');
            return;
          }
          const dt = new DataTransfer();
          dt.items.add(dropped);
          fileInput.files = dt.files;
          updateFileName(section, dropped);
        });
      }

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        submitShopifyContact(section, form);
      });
    });
  }

  window.initializeCustomOrderForms = initializeCustomOrderForms;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeCustomOrderForms());
  } else {
    initializeCustomOrderForms();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeCustomOrderForms(event.target);
  });
})();
